import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Col, Row, Badge } from "react-bootstrap";
import { 
    User, 
    FileText, 
    CreditCard, 
    Users, 
    Shield, 
    Briefcase,
    Check,
    Monitor,
    Clock,
    Calendar,
    Bell,
    Activity,
    ChevronRight,
    Wifi
} from "react-feather";

const Modules = () => {
    const history = useHistory();
    
    // Mock user data - replace with actual user context/API data
    const [userInfo] = useState({
        name: "Zenith Broker",
        type: "Broker", 
        status: "active",
        lastLogin: {
            date: "2024-01-20",
            time: "14:35:22",
            browser: "Chrome 120.0.0.0"
        }
    });

    // Track which modules are being used (active modules show checkmark)
    const [activeModules, setActiveModules] = useState(new Set(['profile', 'underwriting'])); // Example: some modules are active

    const modules = [
        {
            id: 'profile',
            title: 'Profile',
            description: 'Manage your personal information and account settings',
            icon: User,
            color: '#1890ff',
            route: '/dashboard/profile'
        },
        {
            id: 'quotation',
            title: 'Quotation',
            description: 'Create and manage insurance quotes for clients',
            icon: FileText,
            color: '#52c41a',
            route: '/dashboard/quotation'
        },
        {
            id: 'accounting',
            title: 'Accounting',
            description: 'Handle financial transactions and reporting',
            icon: CreditCard,
            color: '#faad14',
            route: '/dashboard/accounting'
        },
        {
            id: 'users',
            title: 'Users',
            description: 'Manage user accounts and permissions',
            icon: Users,
            color: '#722ed1',
            route: '/dashboard/modules/usermanagement'
        },
        {
            id: 'underwriting',
            title: 'Underwriting',
            description: 'Assess and manage insurance risk evaluations',
            icon: Shield,
            color: '#fa541c',
            route: '/dashboard/modules/underwritting'
        },
        {
            id: 'portfolio',
            title: 'Portfolio',
            description: 'View and manage your insurance portfolio',
            icon: Briefcase,
            color: '#13c2c2',
            route: '/dashboard/portfolio'
        }
    ];

    const handleToggleModule = (moduleId) => {
        try {
            setActiveModules(prev => {
                const newSet = new Set(prev);
                if (newSet.has(moduleId)) {
                    newSet.delete(moduleId);
                } else {
                    newSet.add(moduleId);
                }
                return newSet;
            });
        } catch (error) {
            console.error('Error toggling module:', error);
        }
    };

    const handleNavigateToModule = (module) => {
        try {
            console.log(`Navigating to ${module.title} module: ${module.route}`);
            history.push(module.route);
        } catch (error) {
            console.error('Error navigating to module:', error);
        }
    };

    const getStatusColor = (status) => {
        const statusColors = {
            active: 'success',
            inactive: 'danger',
            pending: 'warning',
        };
        return statusColors[status?.toLowerCase()] || 'success';
    };

    const visibleModules = modules;

    return (
        <div className="card p-4" style={{ minHeight: '100vh', background: '#f8fafc' }}>
            {/* Header Section */}
            <div className="mb-4">
                <Row className="align-items-center">
                    <Col md={8}>
                        <div className="d-flex align-items-center mb-3">
                            <User size={32} className="text-primary mr-3" />
                            <div>
                                <h3 className="mb-1" style={{ fontWeight: '600', color: '#1f2937' }}>
                                    {userInfo.name}
                                </h3>
                                <div className="d-flex align-items-center">
                                    <span style={{ fontSize: '1rem', color: '#6b7280', marginRight: '12px' }}>
                                        {userInfo.type}
                                    </span>
                                    <Badge variant={getStatusColor(userInfo.status)} className="text-capitalize">
                                        {userInfo.status}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </Col>
                    
                    {/* Session Widget */}
                    <Col md={4}>
                        <Card 
                            className="h-100" 
                            style={{ 
                                borderRadius: "12px", 
                                border: "1px solid #e5e7eb",
                                background: "#ffffff"
                            }}
                        >
                            <Card.Body className="p-3">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center">
                                        <Wifi size={20} style={{ color: '#22c55e', marginRight: '8px' }} />
                                        <h6 className="mb-0" style={{ fontWeight: '600', color: '#1f2937' }}>Session</h6>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center mb-1">
                                    <Calendar size={14} className="mr-2" style={{ color: '#6b7280' }} />
                                    <small style={{ fontSize: '0.85rem', color: '#374151' }}>
                                        {new Date(userInfo.lastLogin.date).toLocaleDateString('en-US', {
                                            weekday: 'short',
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        })}
                                    </small>
                                </div>
                                <div className="d-flex align-items-center mb-1">
                                    <Clock size={14} className="mr-2" style={{ color: '#6b7280' }} />
                                    <small style={{ fontSize: '0.85rem', color: '#374151' }}>{userInfo.lastLogin.time}</small>
                                </div>
                                <div className="d-flex align-items-center">
                                    <Monitor size={14} className="mr-2" style={{ color: '#6b7280' }} />
                                    <small style={{ fontSize: '0.85rem', color: '#374151' }}>{userInfo.lastLogin.browser}</small>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Modules Section */}
            <div className="mb-4">
                <h4 className="mb-4" style={{ fontWeight: '600', color: '#1f2937' }}>Available Modules</h4>
                
                <Row>
                    {visibleModules.map((module) => {
                        const IconComponent = module.icon;
                        const isActive = activeModules.has(module.id);
                        return (
                            <Col md={4} key={module.id} className="mb-4">
                                <Card 
                                    className="h-100 shadow-sm" 
                                    style={{ 
                                        borderRadius: "12px", 
                                        border: "1px solid #e5e7eb",
                                        transition: "all 0.3s ease",
                                        position: "relative",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => handleNavigateToModule(module)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-4px)";
                                        e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.1)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                                    }}
                                >
                                    {/* Active/Toggle Button */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleModule(module.id);
                                        }}
                                        style={{
                                            position: "absolute",
                                            top: "12px",
                                            right: "12px",
                                            background: isActive ? "#22c55e" : "transparent",
                                            border: isActive ? "none" : "1px solid #d1d5db",
                                            cursor: "pointer",
                                            borderRadius: "50%",
                                            width: "24px",
                                            height: "24px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: isActive ? "#ffffff" : "#9ca3af",
                                            zIndex: 10
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isActive) {
                                                e.target.style.background = "#f3f4f6";
                                                e.target.style.color = "#374151";
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isActive) {
                                                e.target.style.background = "transparent";
                                                e.target.style.color = "#9ca3af";
                                            }
                                        }}
                                    >
                                        <Check size={14} />
                                    </button>

                                    <Card.Body className="p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div 
                                                style={{
                                                    width: "48px",
                                                    height: "48px",
                                                    borderRadius: "12px",
                                                    background: `${module.color}15`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}
                                            >
                                                <IconComponent 
                                                    size={24} 
                                                    style={{ color: module.color }} 
                                                />
                                            </div>
                                        </div>
                                        
                                        <Card.Title 
                                            style={{ 
                                                fontSize: "1.25rem", 
                                                fontWeight: "600",
                                                color: "#1f2937",
                                                marginBottom: "8px"
                                            }}
                                        >
                                            {module.title}
                                        </Card.Title>
                                        
                                        <Card.Text 
                                            style={{ 
                                                fontSize: "0.9rem", 
                                                color: "#6b7280",
                                                lineHeight: "1.5",
                                                marginBottom: "0"
                                            }}
                                        >
                                            {module.description}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </div>

            {/* Bottom Widgets Section */}
            <div className="mb-4">
                <Row>
                    {/* Notifications Widget */}
                    <Col md={6}>
                        <Card 
                            style={{ 
                                borderRadius: "12px", 
                                border: "1px solid #e5e7eb",
                                height: "300px"
                            }}
                        >
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="mb-0" style={{ fontWeight: '600', color: '#1f2937' }}>Notifications</h5>
                                    <div className="d-flex align-items-center">
                                        <Bell size={20} style={{ color: '#6b7280', marginRight: '12px' }} />
                                        <button
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: '#3b82f6',
                                                fontSize: '0.85rem',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.color = '#1d4ed8';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.color = '#3b82f6';
                                            }}
                                        >
                                            View All
                                            <ChevronRight size={14} style={{ marginLeft: '2px' }} />
                                        </button>
                                    </div>
                                </div>
                                <div style={{ height: '220px', overflowY: 'auto' }}>
                                    {/* Sample notifications */}
                                    <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <div className="d-flex align-items-start">
                                            <div 
                                                style={{
                                                    width: "8px",
                                                    height: "8px",
                                                    borderRadius: "50%",
                                                    background: "#22c55e",
                                                    marginTop: "6px",
                                                    marginRight: "12px",
                                                    flexShrink: 0
                                                }}
                                            ></div>
                                            <div>
                                                <p style={{ fontSize: '0.9rem', color: '#374151', margin: 0, lineHeight: '1.4' }}>
                                                    New policy application received
                                                </p>
                                                <small style={{ color: '#6b7280' }}>2 minutes ago</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <div className="d-flex align-items-start">
                                            <div 
                                                style={{
                                                    width: "8px",
                                                    height: "8px",
                                                    borderRadius: "50%",
                                                    background: "#f59e0b",
                                                    marginTop: "6px",
                                                    marginRight: "12px",
                                                    flexShrink: 0
                                                }}
                                            ></div>
                                            <div>
                                                <p style={{ fontSize: '0.9rem', color: '#374151', margin: 0, lineHeight: '1.4' }}>
                                                    Premium payment reminder due
                                                </p>
                                                <small style={{ color: '#6b7280' }}>1 hour ago</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <div className="d-flex align-items-start">
                                            <div 
                                                style={{
                                                    width: "8px",
                                                    height: "8px",
                                                    borderRadius: "50%",
                                                    background: "#3b82f6",
                                                    marginTop: "6px",
                                                    marginRight: "12px",
                                                    flexShrink: 0
                                                }}
                                            ></div>
                                            <div>
                                                <p style={{ fontSize: '0.9rem', color: '#374151', margin: 0, lineHeight: '1.4' }}>
                                                    System maintenance scheduled
                                                </p>
                                                <small style={{ color: '#6b7280' }}>3 hours ago</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Recent Activity Widget */}
                    <Col md={6}>
                        <Card 
                            style={{ 
                                borderRadius: "12px", 
                                border: "1px solid #e5e7eb",
                                height: "300px"
                            }}
                        >
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="mb-0" style={{ fontWeight: '600', color: '#1f2937' }}>Recent Activity</h5>
                                    <div className="d-flex align-items-center">
                                        <Activity size={20} style={{ color: '#6b7280', marginRight: '12px' }} />
                                        <button
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: '#3b82f6',
                                                fontSize: '0.85rem',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.color = '#1d4ed8';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.color = '#3b82f6';
                                            }}
                                        >
                                            View All
                                            <ChevronRight size={14} style={{ marginLeft: '2px' }} />
                                        </button>
                                    </div>
                                </div>
                                <div style={{ height: '220px', overflowY: 'auto' }}>
                                    {/* Sample activities */}
                                    <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <div className="d-flex align-items-start">
                                            <User size={16} style={{ color: '#6b7280', marginTop: '2px', marginRight: '12px', flexShrink: 0 }} />
                                            <div>
                                                <p style={{ fontSize: '0.9rem', color: '#374151', margin: 0, lineHeight: '1.4' }}>
                                                    Updated client profile for John Smith
                                                </p>
                                                <small style={{ color: '#6b7280' }}>5 minutes ago</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <div className="d-flex align-items-start">
                                            <FileText size={16} style={{ color: '#6b7280', marginTop: '2px', marginRight: '12px', flexShrink: 0 }} />
                                            <div>
                                                <p style={{ fontSize: '0.9rem', color: '#374151', margin: 0, lineHeight: '1.4' }}>
                                                    Generated motor insurance quote
                                                </p>
                                                <small style={{ color: '#6b7280' }}>15 minutes ago</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <div className="d-flex align-items-start">
                                            <CreditCard size={16} style={{ color: '#6b7280', marginTop: '2px', marginRight: '12px', flexShrink: 0 }} />
                                            <div>
                                                <p style={{ fontSize: '0.9rem', color: '#374151', margin: 0, lineHeight: '1.4' }}>
                                                    Processed commission payment
                                                </p>
                                                <small style={{ color: '#6b7280' }}>1 hour ago</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default Modules;